import React from "react";
import {
  Leaf, Flower2, Palmtree, Star, Sparkles, Wine, Feather, TreePine, Gem, Music4,
  Minus, CircleDot, Heart, Cake, Baby, PawPrint, Candy, IceCream, Gift,
  Utensils, Snowflake, Sun,
} from "lucide-react";

// Theme → decorative motif mapping. We translate the theme into VISUAL icons
// (not text) so the invite feels themed without ever naming the theme.
export function themeMotifConfig(themeName, customTheme) {
  const t = (themeName || "").toLowerCase();
  const ct = (customTheme || "").toLowerCase();
  const any = (...keys) => keys.some(k => t.includes(k) || ct.includes(k));

  if (any("safari", "animal", "jungle")) return { icons: [Leaf, TreePine, Feather], scatter: [PawPrint, Leaf, TreePine, Feather, Flower2] };
  if (any("boy"))                       return { icons: [Star, Sparkles, Star], scatter: [Star, Sparkles, Baby, Gift, Cake] };
  if (any("girl"))                      return { icons: [Flower2, Sparkles, Flower2], scatter: [Flower2, Heart, Baby, Sparkles, Gift] };
  if (any("tropical", "beach", "palm")) return { icons: [Palmtree, Flower2, Palmtree], scatter: [Palmtree, Sun, Flower2, Leaf, Sparkles] };
  if (any("romantic", "garden", "floral", "flower")) return { icons: [Flower2, Leaf, Flower2], scatter: [Flower2, Heart, Leaf, Sparkles, Feather] };
  if (any("disco", "70s", "neon"))      return { icons: [Gem, Sparkles, Gem], scatter: [Gem, Sparkles, Star, CircleDot, Music4] };
  if (any("vintage"))                   return { icons: [Feather, CircleDot, Feather], scatter: [Feather, CircleDot, Flower2, Sparkles, Leaf] };
  if (any("storybook", "woodland"))     return { icons: [TreePine, Leaf, TreePine], scatter: [TreePine, Leaf, PawPrint, Feather, Sparkles] };
  if (any("festive", "gold", "celebratory")) return { icons: [Sparkles, Star, Sparkles], scatter: [Sparkles, Star, Gift, Cake, Snowflake] };
  if (any("wine", "trattoria", "candle", "dinner")) return { icons: [Wine, Minus, Wine], scatter: [Wine, Utensils, Leaf, Sparkles, Flower2] };
  if (any("music"))                     return { icons: [Music4, Sparkles, Music4], scatter: [Music4, Sparkles, Star, Gem, CircleDot] };
  if (any("candy", "sweet", "sugar"))   return { icons: [Candy, IceCream, Candy], scatter: [Candy, IceCream, Cake, Heart, Sparkles] };
  if (any("birthday", "cake"))          return { icons: [Cake, Sparkles, Cake], scatter: [Cake, Gift, Sparkles, Star, Heart] };
  // Elegant / black tie / minimalist / modern / default: clean rule + dot
  return { icons: null, scatter: [Sparkles, CircleDot, Minus] };
}

export function ThemeOrnament({ config, color = "#1A1A1A", className = "" }) {
  const icons = config?.icons;
  if (!icons) {
    return (
      <div className={`flex items-center justify-center gap-6 ${className}`} aria-hidden="true">
        <span className="h-px w-20" style={{ background: color, opacity: 0.4 }} />
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: color, opacity: 0.6 }} />
        <span className="h-px w-20" style={{ background: color, opacity: 0.4 }} />
      </div>
    );
  }
  return (
    <div className={`flex items-center justify-center gap-5 ${className}`} aria-hidden="true" style={{ color }}>
      <span className="h-px w-14" style={{ background: color, opacity: 0.3 }} />
      {icons.slice(0, 3).map((Icon, idx) => (
        <Icon key={idx} size={22} strokeWidth={1.25} style={{ opacity: 0.75 }} />
      ))}
      <span className="h-px w-14" style={{ background: color, opacity: 0.3 }} />
    </div>
  );
}

// Deterministic pseudo-random so the same event slug gets the same backdrop
function seeded(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return (h / 0x7fffffff);
  };
}

export function ThemeBackdrop({ config, color = "#1A1A1A", seed = "soiree", count = 26, className = "" }) {
  const pool = (config?.scatter && config.scatter.length > 0) ? config.scatter : (config?.icons || []);
  if (!pool.length) return null;
  const rand = seeded(String(seed));
  const items = [];
  for (let i = 0; i < count; i++) {
    const Icon = pool[Math.floor(rand() * pool.length)];
    const top = rand() * 100;
    const left = rand() * 100;
    const size = 14 + Math.floor(rand() * 22); // 14–36
    const rotate = Math.floor(rand() * 60) - 30; // -30°..+30°
    const opacity = 0.08 + rand() * 0.14; // 0.08–0.22
    items.push(
      <Icon
        key={i}
        size={size}
        strokeWidth={1.25}
        style={{
          position: "absolute",
          top: `${top}%`,
          left: `${left}%`,
          transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
          color,
          opacity,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      />
    );
  }
  return (
    <div
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      aria-hidden="true"
      data-testid="theme-backdrop"
    >
      {items}
    </div>
  );
}
